import { useState, useMemo, useEffect } from 'react';
import {
  apiErrorStatus,
  apiErrorText,
  useListPaymentRequestsQuery,
  useCollectPaymentRequestsMutation,
  useSubmitRequestsToCeoMutation,
  useExportApprovedTo1CMutation,
  useListPaymentRegistriesQuery,
  useDecidePaymentRequestMutation,
  useSync1CApiMutation,
  useGetFinancierDashboardQuery,
  type FinPaymentRegistryRow,
  type FinPaymentRequestRow,
  type Export1cResult,
} from '@shared/api';
import { moneyInput, parseMoney } from '@shared/lib/money';
import { useDebouncedValue } from '@shared/lib/useDebouncedValue';
import { Button } from '@shared/ui/Button';
import {
  IconBarChart,
  IconChevronUp,
  IconInbox,
  IconPlus,
  IconSend,
  IconUpload,
  IconX,
} from '@shared/ui/icons';
import { formatMoney } from '../financierFormat';
import { collectResultMessage } from '../collectResult';
import {
  planExport,
  reexportAfterConflict,
  type ExportAsk,
  type ExportKind,
} from '../export1c';
import { Export1CConfirmDialog } from '../Export1CConfirmDialog';
import { registriesHint } from '../registryList';
import { useAgentPermission } from '@entities/user';
import {
  Container,
  HeaderRow,
  TitleBox,
  Title,
  Subtitle,
  ActionsGroup,
  AlertBanner,
  AnalyticsHeroWrapper,
} from './styled';
import { defaultApprovedAmount } from './types';
import { computePipelineMetrics, inPipelineStage } from './pipeline';
import { computeLiquidity } from './liquidity';
import { RequestsPipeline } from './RequestsPipeline';
import { CashThermometer } from './CashThermometer';
import { CompactSummary } from './CompactSummary';
import { RequestsFilterToolbar } from './RequestsFilterToolbar';
import { RequestsTable } from './RequestsTable';
import { RequestsModals } from './RequestsModals';
import { DecisionReasonDialog, type ReasonTarget } from './DecisionReasonDialog';

export function FinancierRequestsView() {
  const { can_write: canWrite } = useAgentPermission('finance');
  const {
    data: dashboardData,
    isLoading: isDashboardLoading,
    isError: isDashboardError,
    refetch: refetchDashboard,
  } = useGetFinancierDashboardQuery();
  // Сервер отдаёт первые 100 реестров и их общее число; остальные — поиском на сервере
  // по номеру или названию.
  const [registrySearch, setRegistrySearch] = useState('');
  const registryQuery = useDebouncedValue(registrySearch.trim(), 300);
  const { data: registryPage } = useListPaymentRegistriesQuery(
    registryQuery ? { search: registryQuery } : undefined
  );
  const foundRegistries = useMemo(() => registryPage?.items ?? [], [registryPage]);

  const [selectedRegistryId, setSelectedRegistryId] = useState<string>('all');
  // Выбранный реестр остаётся в списке, даже если поиск его не находит.
  const [selectedRegistry, setSelectedRegistry] = useState<FinPaymentRegistryRow | null>(null);
  useEffect(() => {
    if (selectedRegistryId === 'all') {
      setSelectedRegistry(null);
      return;
    }
    const row = foundRegistries.find((r) => r.id === selectedRegistryId);
    if (row) setSelectedRegistry(row);
  }, [selectedRegistryId, foundRegistries]);
  const registries = useMemo(
    () =>
      selectedRegistry && !foundRegistries.some((r) => r.id === selectedRegistry.id)
        ? [selectedRegistry, ...foundRegistries]
        : foundRegistries,
    [selectedRegistry, foundRegistries]
  );
  const registryListHint = registriesHint(
    foundRegistries.length,
    registryPage?.total_count ?? null,
    registryQuery
  );
  const [pipelineStage, setPipelineStage] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [moduleFilter, setModuleFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // Сумма к утверждению, которую ввели в строке, — текстом, как в поле. Строки без правки
  // здесь нет: её поле показывает сумму с сервера, и ответ по другой строке её не сбросит.
  const [inlineAmounts, setInlineAmounts] = useState<Record<string, string>>({});
  const [isAnalyticsCollapsed, setIsAnalyticsCollapsed] = useState(false);

  // Сброс выбора заявок при переключении фильтров и реестров (F22)
  useEffect(() => {
    setSelectedIds([]);
  }, [selectedRegistryId, pipelineStage, statusFilter, moduleFilter, search]);

  // Modals state
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createRegistryOpen, setCreateRegistryOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [reconcileModalOpen, setReconcileModalOpen] = useState(false);
  const [markPaidItem, setMarkPaidItem] = useState<FinPaymentRequestRow | null>(null);
  const [exportResult, setExportResult] = useState<Export1cResult | null>(null);
  // Вопрос перед выгрузкой в 1С: что уйдёт в файл. Сервер вызывается только после «Выгрузить».
  const [exportAsk, setExportAsk] = useState<ExportAsk | null>(null);
  // Вопрос — о конкретных строках: сменился реестр или отметки — вопрос устарел.
  useEffect(() => {
    setExportAsk(null);
  }, [selectedRegistryId, selectedIds]);
  const [alertMsg, setAlertMsg] = useState<{ tone: 'success' | 'info' | 'warning'; text: string } | null>(null);
  // Отказ и отсрочка — с причиной: по заявке сотрудника он видит её в своей карточке.
  const [reasonFor, setReasonFor] = useState<ReasonTarget | null>(null);

  // Mutations
  // `data` после смены реестра ещё держит ответ по прежнему; `currentData` — только ответ по
  // выбранному реестру, по нему считается выгрузка.
  const {
    data: requests = [],
    currentData: currentRequests,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useListPaymentRequestsQuery({
    registry_id: selectedRegistryId !== 'all' ? selectedRegistryId : undefined,
  });
  const [collectRequests, { isLoading: isCollecting }] = useCollectPaymentRequestsMutation();
  const [submitToCeo, { isLoading: isSubmitting }] = useSubmitRequestsToCeoMutation();
  const [export1C, { isLoading: isExporting }] = useExportApprovedTo1CMutation();
  const [decideRequest] = useDecidePaymentRequestMutation();
  const [sync1C, { isLoading: isSyncing1C }] = useSync1CApiMutation();

  // Доступная операционная ликвидность с дашборда (F16)
  const isDashboardConfirmed = !isDashboardLoading && !isDashboardError && dashboardData != null;
  const availableCash = isDashboardConfirmed
    ? dashboardData?.accounts_summary?.free_operational_cash ?? 0
    : null;
  // Обязательства — из списка заявок: пока он не пришёл, их не ноль, а «неизвестно».
  const isRequestsConfirmed = !isLoading && !isError;

  // Конвейер: шаг заявки считает одна функция — и для счётчиков, и для отбора по клику.
  const pipelineMetrics = useMemo(() => computePipelineMetrics(requests), [requests]);
  // Число на кнопке «Заявки сотрудников» — сколько их в списке выбранного реестра.
  const staffCount = useMemo(
    () => requests.filter((r) => r.source_module === 'staff').length,
    [requests],
  );

  // Расчёт Балансировщика ликвидности (F16, F18) — только когда пришли и пульт, и список
  // заявок: непогашенный остаток по утверждённым заявкам строго равен сумме остатков по
  // утверждённым строкам.
  const liquidity = computeLiquidity(
    availableCash,
    isRequestsConfirmed ? pipelineMetrics.unpaidTotal : null,
  );
  const isLiquidityConfirmed = liquidity !== null;

  // Фильтрация заявок
  const filtered = useMemo(() => {
    return requests.filter((r) => {
      // 1. Фильтр реестра
      if (selectedRegistryId !== 'all' && r.registry_id !== selectedRegistryId) {
        return false;
      }

      // 2. Фильтр по клику на конвейер
      if (pipelineStage !== 'all' && !inPipelineStage(r, pipelineStage)) return false;

      // 3. Фильтры панели
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (moduleFilter !== 'all' && r.source_module !== moduleFilter) return false;

      // 4. Поиск
      if (search.trim()) {
        const q = search.toLowerCase();
        const matchesNum = r.request_number.toLowerCase().includes(q);
        const matchesCp = r.counterparty_name.toLowerCase().includes(q);
        const matchesInn = r.counterparty_inn?.includes(q) ?? false;
        const matchesPurp = r.purpose.toLowerCase().includes(q);
        const matchesRef = r.source_doc_ref.toLowerCase().includes(q);
        const matchesDoc = r.bank_doc_numbers?.toLowerCase().includes(q) ?? false;
        if (!matchesNum && !matchesCp && !matchesInn && !matchesPurp && !matchesRef && !matchesDoc) {
          return false;
        }
      }

      return true;
    });
  }, [requests, selectedRegistryId, pipelineStage, statusFilter, moduleFilter, search]);

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((r) => selectedIds.includes(r.id));

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filtered.map((r) => r.id));
    }
  };

  const toggleSelectRow = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // Inline approval shortcuts (F25)
  const setPercentAmount = (reqId: string, baseAmount: number, pct: number) => {
    const rawVal = Math.round(baseAmount * pct * 100) / 100;
    const val = Math.max(0, Math.min(baseAmount, rawVal));
    setInlineAmounts((prev) => ({ ...prev, [reqId]: moneyInput(val) }));
  };

  const handleInlineAmountChange = (reqId: string, text: string) => {
    setInlineAmounts((prev) => ({ ...prev, [reqId]: text }));
  };

  /** Решение по заявке; вернёт текст ошибки или `null`, если прошло. */
  const handleInlineDecide = async (
    req: FinPaymentRequestRow,
    decision: 'approved' | 'deferred' | 'rejected',
    reason?: string,
  ): Promise<string | null> => {
    const requested = req.requested_amount || req.amount;
    const typed = inlineAmounts[req.id];
    const parsed = typed === undefined ? defaultApprovedAmount(req) : parseMoney(typed);
    if (decision === 'approved' && (parsed === null || parsed > requested + 0.005)) {
      const text =
        parsed === null
          ? `Заявка ${req.request_number}: сумма к утверждению — числом, например 125 000,50`
          : `Заявка ${req.request_number}: сумма к утверждению больше запрошенной (${formatMoney(requested)})`;
      setAlertMsg({ tone: 'warning', text });
      return text;
    }
    const currentApproved = Math.min(requested, parsed ?? requested);

    try {
      const res = await decideRequest({
        id: req.id,
        decision,
        approved_amount: decision === 'approved' ? currentApproved : undefined,
        carryover_remainder: true,
        comment:
          currentApproved < requested && decision === 'approved'
            ? `Частичный акцепт: ${formatMoney(currentApproved)} из ${formatMoney(requested)}, остаток перенесён в след. реестр`
            : reason?.trim() || undefined,
      }).unwrap();

      const returnedStatus = res?.decided_request?.status;
      const successText =
        decision === 'approved'
          ? returnedStatus === 'ready_for_ceo'
            ? `Заявка ${req.request_number} проверена финансистом и подготовлена к согласованию Гендиректором`
            : `Заявка ${req.request_number} утверждена на ${formatMoney(currentApproved)}`
          : decision === 'deferred'
          ? `Заявка ${req.request_number} отложена`
          : `Заявка ${req.request_number} отклонена`;

      setAlertMsg({
        tone: 'success',
        text: successText,
      });
      // Решение принято: поле строки снова показывает сумму с сервера.
      setInlineAmounts((prev) => {
        const next = { ...prev };
        delete next[req.id];
        return next;
      });
      refetch();
      return null;
    } catch (err: unknown) {
      const text = apiErrorText(err, 'Не удалось выполнить действие по заявке');
      setAlertMsg({
        tone: 'warning',
        text,
      });
      return text;
    }
  };

  // «Отложить» и «Отклонить» сначала спрашивают причину.
  const requestDecision = (
    req: FinPaymentRequestRow,
    decision: 'approved' | 'deferred' | 'rejected',
  ) => {
    if (decision === 'approved') {
      void handleInlineDecide(req, decision);
    } else {
      setReasonFor({ req, decision });
    }
  };

  const handleCollect = async () => {
    try {
      const res = await collectRequests().unwrap();
      // Частичный сбор — «Собрано частично» и что не удалось; старая просрочка — отдельно.
      setAlertMsg(collectResultMessage(res, formatMoney));
      refetch();
    } catch (err: unknown) {
      setAlertMsg({ tone: 'warning', text: apiErrorText(err, 'Не удалось собрать заявки из цехов') });
    }
  };

  const handleSubmitCeo = async () => {
    if (selectedIds.length === 0) return;
    try {
      const res = await submitToCeo({ request_ids: selectedIds }).unwrap();
      setAlertMsg({
        tone: 'success',
        text: `Передано на акцепт Гендиректору: ${res.submitted_count} заявок`,
      });
      setSelectedIds([]);
      refetch();
    } catch (err: unknown) {
      setAlertMsg({
        tone: 'warning',
        text: apiErrorText(err, 'Не удалось передать заявки Гендиректору'),
      });
    }
  };

  // Выгрузка в 1С: отмеченные или весь выбранный реестр (фильтры экрана в нём не
  // участвуют). Сначала вопрос — что уйдёт в файл, и только после «Выгрузить» сервер
  // формирует файл и отмечает заявки выгруженными. Уже выгружавшиеся — отдельным
  // вопросом о повторной выгрузке.
  const handleExport1C = () => {
    const kind: ExportKind | null =
      selectedIds.length > 0 ? 'selected' : selectedRegistryId !== 'all' ? 'registry' : null;
    if (!kind) {
      setAlertMsg({
        tone: 'info',
        text: 'Выберите реестр платежей или отметьте заявки для выгрузки в 1С',
      });
      return;
    }
    // Без списка заявок не посчитать, что уйдёт в файл, — не выгружаем вслепую.
    if (isError && !isFetching) {
      setAlertMsg({
        tone: 'warning',
        text: 'Список заявок не загрузился — обновите его и повторите выгрузку',
      });
      return;
    }
    // Только по ответу для выбранного реестра: пока он не пришёл, на экране ещё прежний.
    if (isFetching || !currentRequests) {
      setAlertMsg({ tone: 'info', text: 'Список заявок обновляется — подождите и повторите' });
      return;
    }
    const registryRow = registries.find((r) => r.id === selectedRegistryId);
    const plan = planExport(
      kind,
      currentRequests,
      selectedIds,
      selectedRegistryId !== 'all'
        ? {
            id: selectedRegistryId,
            label: registryRow ? `${registryRow.registry_number} — ${registryRow.title}` : '',
          }
        : null,
      formatMoney,
    );
    if ('ask' in plan) {
      setExportAsk(plan.ask);
    } else {
      setAlertMsg({ tone: 'info', text: plan.notice });
    }
  };

  const runExport1C = async () => {
    if (!exportAsk) return;
    const ask = exportAsk;
    try {
      const res = await export1C(ask.payload).unwrap();
      setExportAsk(null);
      const skipped = res.skipped_tax_requests ?? [];
      if (res.requests_count > 0) {
        setExportResult(res);
      } else {
        setAlertMsg({
          tone: 'info',
          text:
            skipped.length > 0
              ? `В файл для банка нечего выгружать. Налоги ЕНС проводятся в банк-клиенте:\n${skipped.join('\n')}`
              : 'В файл для банка нечего выгружать: нет согласованных заявок с остатком к оплате.',
        });
      }
      refetch();
    } catch (err: unknown) {
      // 409 — из подтверждённых заявок на сервере выгружать нечего, кроме уже выгружавшихся
      // (список на экране устарел): вопрос о повторной выгрузке тех же заявок.
      if (!ask.reexport && apiErrorStatus(err) === 409) {
        setExportAsk(reexportAfterConflict(ask, apiErrorText(err, 'Платёжки уже выгружались в 1С')));
        refetch();
        return;
      }
      setExportAsk(null);
      setAlertMsg({ tone: 'warning', text: apiErrorText(err, 'Не удалось выгрузить платёжки в 1С') });
    }
  };

  const handleSync1C = async () => {
    try {
      const res = await sync1C({
        registry_id: selectedRegistryId !== 'all' ? selectedRegistryId : undefined,
      }).unwrap();
      setAlertMsg({
        tone: 'success',
        text: `1С API: ${res.message} (режим: ${res.connection_mode})`,
      });
      refetch();
    } catch (err: unknown) {
      setAlertMsg({ tone: 'warning', text: apiErrorText(err, 'Не удалось синхронизироваться с 1С') });
    }
  };

  const handleDownloadExcel = async () => {
    let token =
      localStorage.getItem('osnova_access_token') ||
      localStorage.getItem('osnova.auth.accessToken');
    if (!token) {
      try {
        const raw = localStorage.getItem('osnova.auth');
        if (raw) {
          token = (JSON.parse(raw) as { accessToken?: string })?.accessToken ?? null;
        }
      } catch {
        // ignore json parse error
      }
    }

    const url = `/api/v1/agents/finance/registries/export-excel${
      selectedRegistryId !== 'all' ? `?registry_id=${selectedRegistryId}` : ''
    }`;

    const failed = 'Не удалось скачать реестр в Excel';
    try {
      let res: Response;
      try {
        res = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
      } catch {
        setAlertMsg({ tone: 'warning', text: apiErrorText({ status: 'FETCH_ERROR' }, failed) });
        return;
      }
      if (!res.ok) {
        // Тело ошибки — `{ error: { code, message } }`: разбираем как ответ любого запроса.
        let data: unknown;
        try {
          data = await res.json();
        } catch {
          // не JSON — останется текст по статусу
        }
        setAlertMsg({ tone: 'warning', text: apiErrorText({ status: res.status, data }, failed) });
        return;
      }
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `payment_registry_${selectedRegistryId}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (err: unknown) {
      setAlertMsg({ tone: 'warning', text: apiErrorText(err, failed) });
    }
  };

  return (
    <Container>
      <HeaderRow>
        <TitleBox>
          <Title>Фабрика платежей и Реестр казначейства</Title>
          <Subtitle>
            Формирование реестра дня, 4 гейта финконтроля, инлайн-акцепт с автопереносом остатка (-ОСТ), выгрузка в 1С и сверка с выпиской.
          </Subtitle>
        </TitleBox>

        <ActionsGroup>
          <Button
            $variant="secondary"
            onClick={handleCollect}
            disabled={isCollecting || !canWrite}
          >
            <IconInbox size={16} />
            {isCollecting ? 'Сбор…' : 'Собрать из цехов'}
          </Button>

          <Button
            $variant="primary"
            onClick={handleSubmitCeo}
            disabled={selectedIds.length === 0 || isSubmitting || !canWrite}
          >
            <IconSend size={16} />
            {isSubmitting ? 'Отправка…' : `Передать Гендиректору (${selectedIds.length})`}
          </Button>

          <Button
            $variant="secondary"
            onClick={handleExport1C}
            disabled={isExporting || !canWrite}
          >
            <IconUpload size={16} />
            {isExporting ? 'Выгрузка…' : 'Выгрузить в 1С'}
          </Button>

          <Button
            $variant="secondary"
            onClick={() => setCreateModalOpen(true)}
            disabled={!canWrite}
          >
            <IconPlus size={16} />
            Новая заявка
          </Button>

          <Button
            $variant="ghost"
            onClick={() => setIsAnalyticsCollapsed((prev) => !prev)}
            aria-expanded={!isAnalyticsCollapsed}
            aria-controls="financier-analytics-hero"
            title={isAnalyticsCollapsed ? 'Развернуть блок аналитики' : 'Свернуть блок аналитики'}
          >
            {isAnalyticsCollapsed ? <IconBarChart size={16} /> : <IconChevronUp size={16} />}
            {isAnalyticsCollapsed ? 'Развернуть аналитику' : 'Свернуть аналитику'}
          </Button>
        </ActionsGroup>
      </HeaderRow>

      {alertMsg && (
        <AlertBanner $tone={alertMsg.tone}>
          <span>{alertMsg.text}</span>
          <Button $variant="ghost" $size="sm" onClick={() => setAlertMsg(null)} aria-label="Закрыть">
            <IconX size={14} />
          </Button>
        </AlertBanner>
      )}

      {/* 1 & 2. Аналитический блок (Конвейер + Балансировщик) / Компактный вид */}
      {isAnalyticsCollapsed ? (
        <CompactSummary
          pipelineStage={pipelineStage}
          metrics={isRequestsConfirmed ? pipelineMetrics : null}
          isDashboardLoading={isDashboardLoading}
          isDashboardError={isDashboardError}
          availableCash={availableCash}
          coveragePct={isLiquidityConfirmed ? liquidity.coveragePct : null}
          isRequestsError={isError}
          onRetryRequests={() => refetch()}
          onExpand={() => setIsAnalyticsCollapsed(false)}
        />
      ) : (
        <AnalyticsHeroWrapper id="financier-analytics-hero">
          <RequestsPipeline
            pipelineStage={pipelineStage}
            onStageChange={setPipelineStage}
            metrics={isRequestsConfirmed ? pipelineMetrics : null}
          />

          <CashThermometer
            liquidity={liquidity}
            isDashboardLoading={isDashboardLoading}
            isDashboardError={isDashboardError}
            availableCash={availableCash}
            remainingToDebit={isRequestsConfirmed ? pipelineMetrics.unpaidTotal : null}
            totalPaidInView={isRequestsConfirmed ? pipelineMetrics.paidTotal : null}
            isRequestsError={isError}
            onRefetchDashboard={() => refetchDashboard()}
            onRetryRequests={() => refetch()}
          />
        </AnalyticsHeroWrapper>
      )}

      {/* 3. Селектор Реестров платежей и панель действий */}
      <RequestsFilterToolbar
        selectedRegistryId={selectedRegistryId}
        onSelectRegistry={setSelectedRegistryId}
        registries={registries}
        registrySearch={registrySearch}
        onRegistrySearchChange={setRegistrySearch}
        registriesHint={registryListHint}
        onSync1C={handleSync1C}
        isSyncing1C={isSyncing1C}
        onOpenImport={() => setImportModalOpen(true)}
        onOpenReconcile={() => setReconcileModalOpen(true)}
        onDownloadExcel={handleDownloadExcel}
        onOpenCreateRegistry={() => setCreateRegistryOpen(true)}
        search={search}
        onSearchChange={setSearch}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        moduleFilter={moduleFilter}
        onModuleFilterChange={setModuleFilter}
        staffCount={isRequestsConfirmed ? staffCount : null}
        canWrite={canWrite}
      />

      {/* 4. Интерактивная таблица заявок */}
      <RequestsTable
        filtered={filtered}
        isLoading={isLoading}
        isError={isError}
        refetch={refetch}
        selectedIds={selectedIds}
        allFilteredSelected={allFilteredSelected}
        onToggleSelectAll={toggleSelectAll}
        onToggleSelectRow={toggleSelectRow}
        inlineAmounts={inlineAmounts}
        onInlineAmountChange={handleInlineAmountChange}
        onSetPercentAmount={setPercentAmount}
        onInlineDecide={requestDecision}
        onSetMarkPaidItem={setMarkPaidItem}
        onGateAlert={setAlertMsg}
        canWrite={canWrite}
      />

      {/* Модальные окна */}
      <RequestsModals
        exportResult={exportResult}
        onCloseExport={() => setExportResult(null)}
        createModalOpen={createModalOpen}
        onCloseCreateModal={() => setCreateModalOpen(false)}
        createRegistryOpen={createRegistryOpen}
        onCloseCreateRegistry={() => setCreateRegistryOpen(false)}
        onRegistryCreated={(newId) => {
          setRegistrySearch('');
          setSelectedRegistryId(newId);
          refetch();
        }}
        importModalOpen={importModalOpen}
        onCloseImportModal={() => setImportModalOpen(false)}
        reconcileModalOpen={reconcileModalOpen}
        onCloseReconcileModal={() => setReconcileModalOpen(false)}
        markPaidItem={markPaidItem}
        onCloseMarkPaidItem={() => setMarkPaidItem(null)}
        selectedRegistryId={selectedRegistryId}
        onSuccess={() => refetch()}
      />

      <Export1CConfirmDialog
        ask={exportAsk}
        busy={isExporting}
        onCancel={() => setExportAsk(null)}
        onConfirm={() => void runExport1C()}
      />

      {reasonFor && (
        <DecisionReasonDialog
          key={`${reasonFor.req.id}:${reasonFor.decision}`}
          target={reasonFor}
          onClose={() => setReasonFor(null)}
          onConfirm={async (reason) => {
            const failed = await handleInlineDecide(reasonFor.req, reasonFor.decision, reason);
            if (!failed) setReasonFor(null);
            return failed;
          }}
        />
      )}
    </Container>
  );
}
